<template>
  <div class="retailcrm-report">
    <div class="header-main">
      <div class="hm-left">
        <div class="header-main__title-wrap">
          <h1 class="header-main__title">Сверка RetailCRM и МойСклад</h1>
        </div>
      </div>
    </div>

    <div class="report-controls">
      <!-- Основной блок: выбор периода и поиск -->
      <div class="filter-section filter-section-main">
        <div class="filter-group multiselect-wrap">
          <label class="filter-label">Периоды:</label>
          <div class="multiselect-trigger" @click.stop="openDropdown = openDropdown === 'periods' ? '' : 'periods'" :class="{ open: openDropdown === 'periods' }">
            <span class="multiselect-label">{{ selectedPeriods.length ? `Выбрано: ${selectedPeriods.length}` : '— выберите периоды —' }}</span>
            <span class="multiselect-arrow">▾</span>
          </div>
          <div v-show="openDropdown === 'periods'" class="multiselect-dropdown multiselect-dropdown-periods" @click.stop>
            <div v-if="loadingList" class="multiselect-loading">загрузка...</div>
            <template v-else>
              <label class="multiselect-item multiselect-select-all">
                <input type="checkbox" :checked="selectedPeriods.length === reports.length && reports.length > 0" @change="toggleAllPeriods" />
                <span>Выбрать все</span>
              </label>
              <label v-for="r in reports" :key="r.period" class="multiselect-item">
                <input type="checkbox" :checked="selectedPeriods.includes(r.period)" @change="togglePeriod(r.period)" />
                <span>{{ r.period }} ({{ formatDate(r.modified) }})</span>
              </label>
            </template>
          </div>
          <span v-if="loadingList" class="loading-label">загрузка...</span>
        </div>
        <div class="filter-group">
          <label class="filter-label">Поиск:</label>
          <span class="search-wrap">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Номер или ID"
              class="search-input"
              @keyup.enter="loadData(1)"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="btn-clear-search"
              title="Очистить"
              @click="clearSearch"
            >×</button>
          </span>
        </div>
      </div>

      <!-- Фильтры по документам -->
      <div class="filter-section">
        <div class="filter-section-title">Документы</div>
        <div class="filter-grid filter-grid-6">
          <div class="filter-group">
            <label class="filter-label">Результат</label>
            <select v-model="statusFilter" @change="loadData(1)" class="filter-select">
              <option :value="null">Все</option>
              <option value="соответствует">Соответствует</option>
              <option value="не найдено в МС">Не найдено в МС</option>
              <option value="не соответствует">Не соответствует</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Группа</label>
            <select v-model="groupFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="1">1) Без движения товара (согласование)</option>
              <option value="2">2) доставляется и отгружен полностью</option>
              <option value="3">3) доставлен и отгружен полностью</option>
              <option value="3a">3а) магазин не отгружает</option>
              <option value="3b">3б) обязателен возврат или приемка</option>
              <option value="4">4) доставлен частично + возврат</option>
              <option value="5">5) полный возврат с отгрузкой</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Отгрузка</label>
            <select v-model="demandFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">Есть</option>
              <option value="false">Нет</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Возврат</label>
            <select v-model="returnFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">Есть</option>
              <option value="false">Нет</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Розн. возврат</label>
            <select v-model="retailReturnFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">Есть</option>
              <option value="false">Нет</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">UUID (RetailCRM)</label>
            <select v-model="uuidFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">С UUID</option>
              <option value="false">Без UUID</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Фильтры по статусам и складам (мультиселекты) -->
      <div class="filter-section">
        <div class="filter-section-title">Статусы и склады</div>
        <div class="filter-grid filter-grid-2">
          <div class="filter-group multiselect-wrap">
            <label class="filter-label">Статус CRM</label>
            <div class="multiselect-trigger" @click.stop="openDropdown = openDropdown === 'retail_status' ? '' : 'retail_status'" :class="{ open: openDropdown === 'retail_status' }">
              <span class="multiselect-label">{{ retailStatusFilter.length ? `${retailStatusFilter.length} выбрано` : 'Все статусы' }}</span>
              <span class="multiselect-arrow">▾</span>
            </div>
            <div v-show="openDropdown === 'retail_status'" class="multiselect-dropdown multiselect-dropdown-list" @click.stop>
              <label v-for="s in filterOptions.retail_statuses" :key="s" class="multiselect-item">
                <input type="checkbox" :checked="retailStatusFilter.includes(s)" @change="toggleFilterMulti('retail_status', s)" />
                <span>{{ s }}</span>
              </label>
              <p v-if="!filterOptions.retail_statuses.length" class="multiselect-empty">Сначала выберите период</p>
            </div>
          </div>
          <div class="filter-group multiselect-wrap">
            <label class="filter-label">Статус МС</label>
            <div class="multiselect-trigger" @click.stop="openDropdown = openDropdown === 'moysklad_status' ? '' : 'moysklad_status'" :class="{ open: openDropdown === 'moysklad_status' }">
              <span class="multiselect-label">{{ moyskladStatusFilter.length ? `${moyskladStatusFilter.length} выбрано` : 'Все статусы' }}</span>
              <span class="multiselect-arrow">▾</span>
            </div>
            <div v-show="openDropdown === 'moysklad_status'" class="multiselect-dropdown multiselect-dropdown-list" @click.stop>
              <label v-for="s in filterOptions.moysklad_statuses" :key="s" class="multiselect-item">
                <input type="checkbox" :checked="moyskladStatusFilter.includes(s)" @change="toggleFilterMulti('moysklad_status', s)" />
                <span>{{ s }}</span>
              </label>
              <p v-if="!filterOptions.moysklad_statuses.length" class="multiselect-empty">Сначала выберите период</p>
            </div>
          </div>
          <div class="filter-group multiselect-wrap">
            <label class="filter-label">Склад CRM</label>
            <div class="multiselect-trigger" @click.stop="openDropdown = openDropdown === 'retail_store' ? '' : 'retail_store'" :class="{ open: openDropdown === 'retail_store' }">
              <span class="multiselect-label">{{ retailStoreFilter.length ? `${retailStoreFilter.length} выбрано` : 'Все склады' }}</span>
              <span class="multiselect-arrow">▾</span>
            </div>
            <div v-show="openDropdown === 'retail_store'" class="multiselect-dropdown multiselect-dropdown-list" @click.stop>
              <label v-for="s in filterOptions.retail_stores" :key="s" class="multiselect-item">
                <input type="checkbox" :checked="retailStoreFilter.includes(s)" @change="toggleFilterMulti('retail_store', s)" />
                <span>{{ s }}</span>
              </label>
              <p v-if="!filterOptions.retail_stores.length" class="multiselect-empty">Сначала выберите период</p>
            </div>
          </div>
          <div class="filter-group multiselect-wrap">
            <label class="filter-label">Склад МС</label>
            <div class="multiselect-trigger" @click.stop="openDropdown = openDropdown === 'moysklad_store' ? '' : 'moysklad_store'" :class="{ open: openDropdown === 'moysklad_store' }">
              <span class="multiselect-label">{{ moyskladStoreFilter.length ? `${moyskladStoreFilter.length} выбрано` : 'Все склады' }}</span>
              <span class="multiselect-arrow">▾</span>
            </div>
            <div v-show="openDropdown === 'moysklad_store'" class="multiselect-dropdown multiselect-dropdown-list" @click.stop>
              <label v-for="s in filterOptions.moysklad_stores" :key="s" class="multiselect-item">
                <input type="checkbox" :checked="moyskladStoreFilter.includes(s)" @change="toggleFilterMulti('moysklad_store', s)" />
                <span>{{ s }}</span>
              </label>
              <p v-if="!filterOptions.moysklad_stores.length" class="multiselect-empty">Сначала выберите период</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Услуги / доп. товары в составе (CRM или МС) -->
      <div class="filter-section filter-section-wide">
        <div class="filter-section-title">Услуги в составе</div>
        <div class="filter-group multiselect-wrap">
          <label class="filter-label">Позиции</label>
          <div
            class="multiselect-trigger"
            @click.stop="openDropdown = openDropdown === 'service_products' ? '' : 'service_products'"
            :class="{ open: openDropdown === 'service_products' }"
          >
            <span class="multiselect-label">{{ serviceProductFilter.length ? `${serviceProductFilter.length} выбрано` : 'Все' }}</span>
            <span class="multiselect-arrow">▾</span>
          </div>
          <div v-show="openDropdown === 'service_products'" class="multiselect-dropdown multiselect-dropdown-list multiselect-dropdown-wide" @click.stop>
            <label v-for="opt in serviceProductOptions" :key="opt.id" class="multiselect-item multiselect-item-text">
              <input type="checkbox" :checked="serviceProductFilter.includes(opt.id)" @change="toggleServiceProductFilter(opt.id)" />
              <span>{{ opt.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Фильтры по совпадениям -->
      <div class="filter-section">
        <div class="filter-section-title">Совпадения</div>
        <div class="filter-grid filter-grid-4">
          <div class="filter-group">
            <label class="filter-label">Статусы</label>
            <select v-model="matchStatusFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">Совпад.</option>
              <option value="false">Не совпад.</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Товары</label>
            <select v-model="matchProductsFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">Совпад.</option>
              <option value="false">Не совпад.</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Склады</label>
            <select v-model="matchStoresFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">Совпад.</option>
              <option value="false">Не совпад.</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Группа</label>
            <select v-model="matchGroupFilter" @change="loadData(1)" class="filter-select">
              <option value="">Все</option>
              <option value="true">Совпад.</option>
              <option value="false">Не совпад.</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Кнопки действий -->
      <div class="filter-section filter-section-actions">
        <button class="btn-search" @click="loadData(1)">Найти</button>
        <button
          v-if="hasAnyFilter"
          type="button"
          class="btn-clear"
          @click="clearFilters"
        >
          Сбросить фильтры
        </button>
        <button
          v-if="reportMeta.period && reportData.total_filtered > 0"
          type="button"
          class="btn-select-all-filter"
          :disabled="selectAllByFilterLoading"
          @click="selectAllByFilter"
        >
          {{ selectAllByFilterLoading ? 'Загрузка...' : 'Выделить все по фильтру' }}
        </button>
      </div>
    </div>

    <template v-if="selectedPeriods.length && reportMeta.period">
      <div class="report-summary">
        <span class="summary-item">Период: {{ reportMeta.period_label || reportMeta.period }}</span>
        <span class="summary-item">Сформирован: {{ reportMeta.generated_at }}</span>
        <span class="summary-item">Всего заказов: {{ reportMeta.total_orders }}</span>
        <span
          class="summary-item summary-ok summary-click"
          :class="{ 'summary-active': statusFilter === 'соответствует' }"
          @click="toggleStatusFilter('соответствует')"
        >
          Соответствует: {{ reportMeta.summary['соответствует'] || 0 }}
        </span>
        <span
          class="summary-item summary-warn summary-click"
          :class="{ 'summary-active': statusFilter === 'не найдено в МС' }"
          @click="toggleStatusFilter('не найдено в МС')"
        >
          Не найдено в МС: {{ reportMeta.summary['не найдено в МС'] || 0 }}
        </span>
        <span
          class="summary-item summary-err summary-click"
          :class="{ 'summary-active': statusFilter === 'не соответствует' }"
          @click="toggleStatusFilter('не соответствует')"
        >
          Не соответствует: {{ reportMeta.summary['не соответствует'] || 0 }}
        </span>
      </div>

      <div class="table-wrap">
        <!-- Панель пересравления выбранных заказов -->
        <div v-if="selectedForRecompare.length > 0" class="recompare-panel">
          <div class="recompare-info">
            Для пересравления: <strong>{{ selectedForRecompare.length }}</strong> заказов
            <button type="button" class="btn-clear-selection" @click="clearRecompareSelection">Снять выделение</button>
          </div>
          <div class="recompare-actions">
            <button
              type="button"
              class="btn-recompare"
              :disabled="recompareLoading"
              @click="runRecompare"
            >
              {{ recompareLoading ? 'Пересравление...' : 'Пересравнить выбранные' }}
            </button>
          </div>
          <div v-if="recompareResult" :class="['recompare-result', recompareResult.success ? 'result-success' : 'result-error']">
            {{ recompareResult.message }}
            <span v-if="recompareResult.updated != null"> (обновлено: {{ recompareResult.updated }})</span>
          </div>
        </div>
        <!-- Панель массового обновления -->
        <div v-if="selectedOrders.length > 0" class="bulk-update-panel">
          <div class="bulk-info">
            Выбрано заказов: <strong>{{ selectedOrders.length }}</strong>
            <button class="btn-clear-selection" @click="clearSelection">Снять выделение</button>
          </div>
          <div class="bulk-controls">
            <input
              v-model="bulkFieldValue"
              type="text"
              class="bulk-input"
              placeholder="Введите значение для поля"
            />
            <button
              class="btn-bulk-update"
              @click="bulkUpdateField"
              :disabled="bulkUpdating || !bulkFieldValue.trim()"
            >
              {{ bulkUpdating ? 'Обновление...' : 'Обновить в МойСклад' }}
            </button>
          </div>
          <div v-if="bulkUpdateResult" :class="['bulk-result', bulkUpdateResult.success ? 'result-success' : 'result-error']">
            {{ bulkUpdateResult.message }}
          </div>
        </div>

        <table class="report-table">
          <thead>
            <tr>
              <th class="cell-checkbox">
                <input
                  type="checkbox"
                  :checked="allVisibleSelected"
                  @change="toggleSelectAll"
                  title="Выбрать все на странице"
                />
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'number' }" @click="setSort('number')">
                № заказа <span v-if="sortBy === 'number'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'status' }" @click="setSort('status')">
                Статус <span v-if="sortBy === 'status'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'retail_status' }" @click="setSort('retail_status')">
                Статус RetailCRM <span v-if="sortBy === 'retail_status'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'moysklad_status' }" @click="setSort('moysklad_status')">
                Статус МойСклад <span v-if="sortBy === 'moysklad_status'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'retail_store' }" @click="setSort('retail_store')" title="Сортировка по складу CRM">
                Склады (CRM / МС) <span v-if="sortBy === 'retail_store'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-composition">Товары в RetailCRM</th>
              <th class="th-composition" title="Отгрузки заказа покупателя в МС или состав розничной продажи (retaildemand)">Отгрузки</th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'status_group' }" @click="setSort('status_group')">
                Группа <span v-if="sortBy === 'status_group'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'has_demand' }" @click="setSort('has_demand')" title="По отгрузке">
                Движение <span v-if="sortBy === 'has_demand'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'total_sum_retail' }" @click="setSort('total_sum_retail')" title="По сумме RetailCRM">
                Суммы <span v-if="sortBy === 'total_sum_retail'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="th-sortable" :class="{ 'sorted': sortBy === 'status_match' }" @click="setSort('status_match')" title="По совпадению статусов">
                Совпадения <span v-if="sortBy === 'status_match'" class="sort-arrow">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="cell-verified-header">Проверено</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="14" class="cell-loading">Загрузка...</td>
            </tr>
            <tr v-else-if="!orders.length">
              <td colspan="14" class="cell-empty">Нет данных</td>
            </tr>
            <tr
              v-for="row in orders"
              :key="row.id || row.number"
              :class="[row.status, { 'row-selected': isSelected(row) }]"
              @click="openModal(row)"
              class="clickable-row"
            >
              <td class="cell-checkbox" @click.stop>
                <input
                  type="checkbox"
                  :checked="isSelected(row)"
                  @change="toggleSelect(row)"
                />
              </td>
              <td class="cell-number">{{ row.number }}</td>
              <td class="cell-status">
                <span :class="['status-badge', statusClass(row.status)]">{{ row.status }}</span>
              </td>
              <td>
                <a
                  v-if="row.id"
                  :href="retailOrderUrl(row.id)"
                  target="_blank"
                  rel="noopener"
                  class="status-link"
                  @click.stop
                >{{ row.retail_status || '—' }}</a>
                <span v-else>{{ row.retail_status || '—' }}</span>
              </td>
              <td>
                <a
                  v-if="row.moysklad_uuid"
                  :href="moyskladOrderUrl(row.moysklad_uuid)"
                  target="_blank"
                  rel="noopener"
                  class="status-link"
                  @click.stop
                >{{ row.moysklad_status || '—' }}</a>
                <span v-else>{{ row.moysklad_status || '—' }}</span>
              </td>
              <td>
                <div class="store-info">CRM: <b>{{ row.retail_store || '—' }}</b></div>
                <div class="store-info">МС: <b>{{ row.moysklad_store || '—' }}</b></div>
              </td>
              <td class="cell-composition" @click.stop>
                <template v-if="getRetailProductLines(row).length">
                  <div
                    v-for="(p, ri) in getRetailProductLines(row)"
                    :key="'rcomp-' + row.id + '-' + ri"
                    class="composition-line"
                    :class="{ 'composition-line-canceled': p.isCanceled }"
                  >{{ p.name || '—' }}</div>
                </template>
                <span v-else class="composition-empty">Нет товаров</span>
              </td>
              <td class="cell-composition" @click.stop>
                <template v-if="getMoyskladCompositionLines(row).length">
                  <div
                    v-for="(p, di) in getMoyskladCompositionLines(row)"
                    :key="'dcomp-' + row.id + '-' + di"
                    class="composition-line"
                  >{{ p.name || '—' }}</div>
                </template>
                <span v-else class="composition-empty">Нет отгрузок / розн. продажи</span>
              </td>
              <td style="text-align: center;">
                <span v-if="row.status_group" class="badge-group">{{ row.status_group }}</span>
                <span v-else>—</span>
              </td>
              <td style="white-space: nowrap;">
                <span :class="['badge-move', row.has_demand ? 'move-yes' : 'move-no']" title="Отгрузка">О</span>
                <span :class="['badge-move', row.has_return ? 'move-yes' : 'move-no']" title="Возврат">В</span>
                <span :class="['badge-move', row.has_retail_return ? 'move-yes' : 'move-no']" title="Розн. возврат">РВ</span>
              </td>
              <td style="white-space: nowrap;">
                <template v-if="row.total_sum">
                  <div>{{ row.total_sum.retail }} / {{ row.total_sum.moysklad }}</div>
                  <div v-if="row.total_sum.difference > 0" class="sum-diff">Δ {{ row.total_sum.difference }}</div>
                </template>
                <span v-else>—</span>
              </td>
              <td style="white-space: nowrap;">
                <template v-if="row.summary">
                  <span :class="row.summary.status_match ? 'match-yes' : 'match-no'" title="Статусы">С</span>
                  <span :class="row.summary.products_match ? 'match-yes' : 'match-no'" title="Товары">Т</span>
                  <span :class="row.summary.stores_match ? 'match-yes' : 'match-no'" title="Склады">С</span>
                  <span :class="row.summary.group_match ? 'match-yes' : 'match-no'" title="Группа">Г</span>
                </template>
                <span v-else>—</span>
              </td>
              <td class="cell-verified" @click.stop>
                <input
                  type="checkbox"
                  :checked="row.verified === true"
                  :disabled="verifiedSaving === row.id"
                  title="Проверено"
                  @change="setVerified(row, $event.target.checked)"
                />
              </td>
              <td>
                <button class="btn-details" @click.stop="openModal(row)">Подробнее</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <span class="pagination-info">
          Показано {{ (reportData.page - 1) * reportData.per_page + 1 }}–{{ Math.min(reportData.page * reportData.per_page, reportData.total_filtered) }} из {{ reportData.total_filtered }}
        </span>
        <button :disabled="reportData.page <= 1" @click="loadData(reportData.page - 1)">Назад</button>
        <span class="page-num">Стр. {{ reportData.page }} из {{ reportData.total_pages || 1 }}</span>
        <button :disabled="reportData.page >= reportData.total_pages" @click="loadData(reportData.page + 1)">Вперёд</button>
        <select v-model.number="perPage" @change="loadData(1)" class="per-page-select">
          <option :value="50">50 на стр.</option>
          <option :value="100">100 на стр.</option>
          <option :value="200">200 на стр.</option>
        </select>
      </div>
    </template>

    <p v-else-if="!loadingList && !selectedPeriods.length" class="hint">Выберите один или несколько периодов, чтобы загрузить отчёт.</p>

    <!-- Модальное окно детальной информации -->
    <div v-if="modalOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Заказ {{ modalData.number }}</h2>
          <button class="modal-close" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <!-- Основная информация -->
          <div class="modal-section">
            <h3>Основная информация</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">ID в RetailCRM:</span>
                <span class="info-value">{{ modalData.retail_order_id || modalData.id }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">UUID в МойСклад:</span>
                <span class="info-value">{{ modalData.moysklad_uuid || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Результат сверки:</span>
                <span :class="['info-value', 'status-badge', statusClass(modalData.status)]">{{ modalData.status }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Группа статуса:</span>
                <span class="info-value"><span v-if="modalData.status_group" class="badge-group">{{ modalData.status_group }}</span><span v-else>—</span></span>
              </div>
            </div>
          </div>

          <!-- Статусы -->
          <div class="modal-section">
            <h3>Статусы</h3>
            <div class="compare-grid">
              <div class="compare-col">
                <h4>RetailCRM</h4>
                <div class="compare-value">{{ modalData.retail_status || '—' }}</div>
                <template v-if="compareResult && compareResult.status && compareResult.status.retail">
                  <div class="compare-detail">Код: {{ compareResult.status.retail.code || '—' }}</div>
                  <div class="compare-detail">Группа: {{ compareResult.status.retail.group || '—' }}</div>
                </template>
              </div>
              <div class="compare-col compare-middle">
                <div :class="modalData.summary && modalData.summary.status_match ? 'match-icon-yes' : 'match-icon-no'">
                  {{ modalData.summary && modalData.summary.status_match ? '✓' : '✗' }}
                </div>
                <div class="compare-method" v-if="compareResult && compareResult.status">
                  {{ compareResult.status.comparison_method }}
                </div>
              </div>
              <div class="compare-col">
                <h4>МойСклад</h4>
                <div class="compare-value">{{ modalData.moysklad_status || '—' }}</div>
                <template v-if="compareResult && compareResult.status && compareResult.status.moysklad">
                  <div class="compare-detail">ID: {{ compareResult.status.moysklad.id || '—' }}</div>
                </template>
              </div>
            </div>
          </div>

          <!-- Склады -->
          <div class="modal-section">
            <h3>Склады</h3>
            <div class="compare-grid">
              <div class="compare-col">
                <h4>RetailCRM</h4>
                <div class="compare-value">{{ modalData.retail_store || '—' }}</div>
                <template v-if="compareResult && compareResult.stores && compareResult.stores.retail">
                  <div class="compare-detail">Код: {{ compareResult.stores.retail.code || '—' }}</div>
                </template>
              </div>
              <div class="compare-col compare-middle">
                <div :class="modalData.summary && modalData.summary.stores_match ? 'match-icon-yes' : 'match-icon-no'">
                  {{ modalData.summary && modalData.summary.stores_match ? '✓' : '✗' }}
                </div>
                <div class="compare-method" v-if="compareResult && compareResult.stores">
                  {{ compareResult.stores.comparison_method }}
                </div>
              </div>
              <div class="compare-col">
                <h4>МойСклад</h4>
                <div class="compare-value">{{ modalData.moysklad_store || '—' }}</div>
                <template v-if="compareResult && compareResult.stores && compareResult.stores.moysklad">
                  <div class="compare-detail">ID: {{ compareResult.stores.moysklad.id || '—' }}</div>
                </template>
              </div>
            </div>
          </div>

          <!-- Движение товаров -->
          <div class="modal-section">
            <h3>Движение товаров</h3>
            <div class="movement-grid">
              <div class="movement-item">
                <span class="movement-label">Отгрузка:</span>
                <span :class="['movement-value', hasMovement('demand') ? 'move-yes' : 'move-no']">
                  {{ hasMovement('demand') ? 'Есть' : 'Нет' }}
                </span>
              </div>
              <div class="movement-item">
                <span class="movement-label">Возврат:</span>
                <span :class="['movement-value', hasMovement('return') ? 'move-yes' : 'move-no']">
                  {{ hasMovement('return') ? 'Есть' : 'Нет' }}
                </span>
              </div>
              <div class="movement-item">
                <span class="movement-label">Розничная продажа:</span>
                <span :class="['movement-value', hasMovement('retail_demand') ? 'move-yes' : 'move-no']">
                  {{ hasMovement('retail_demand') ? 'Есть' : 'Нет' }}
                </span>
              </div>
              <div class="movement-item">
                <span class="movement-label">Розничный возврат:</span>
                <span :class="['movement-value', hasMovement('retail_return') ? 'move-yes' : 'move-no']">
                  {{ hasMovement('retail_return') ? 'Есть' : 'Нет' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Валидация группы -->
          <div class="modal-section" v-if="modalData.group_validation">
            <h3>Валидация по группе</h3>
            <div :class="['validation-result', modalData.group_validation.valid ? 'validation-ok' : 'validation-fail']">
              <span class="validation-icon">{{ modalData.group_validation.valid ? '✓' : '✗' }}</span>
              <span class="validation-message">{{ modalData.group_validation.message || 'Проверка пройдена' }}</span>
            </div>
          </div>

          <!-- Суммы -->
          <div class="modal-section">
            <h3>Суммы заказа</h3>
            <div class="sums-grid" v-if="modalData.total_sum">
              <div class="sum-item">
                <span class="sum-label">RetailCRM:</span>
                <span class="sum-value">{{ modalData.total_sum.retail }} BYN</span>
              </div>
              <div class="sum-item">
                <span class="sum-label">МойСклад:</span>
                <span class="sum-value">{{ modalData.total_sum.moysklad }} BYN</span>
              </div>
              <div class="sum-item" v-if="modalData.total_sum.difference > 0">
                <span class="sum-label">Разница:</span>
                <span class="sum-value sum-diff">{{ modalData.total_sum.difference }} BYN</span>
              </div>
            </div>
            <div v-else class="no-data">Нет данных о суммах</div>
          </div>

          <!-- Товары RetailCRM -->
          <div class="modal-section">
            <h3>Товары в RetailCRM <span class="count-badge" v-if="retailProducts.length">({{ retailProducts.length }})</span></h3>
            <div class="products-table-wrap" v-if="retailProducts.length">
              <table class="products-table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Статус</th>
                    <th>Кол-во</th>
                    <th>Цена</th>
                    <th>Сумма</th>
                    <th>ExternalId</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in retailProducts" :key="'rp-'+i" :class="{ 'canceled-row': p.isCanceled }">
                    <td>{{ p.name }}</td>
                    <td>
                      <span v-if="p.status" :class="['product-status', getProductStatusClass(p.status)]">
                        {{ getProductStatusLabel(p.status) }}
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td>{{ p.quantity }}</td>
                    <td>{{ p.price }}</td>
                    <td>{{ p.sum }}</td>
                    <td>{{ p.externalId || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-data">Нет товаров</div>
          </div>

          <!-- Товары МойСклад -->
          <div class="modal-section">
            <h3>Товары в МойСклад <span class="count-badge" v-if="moyskladProducts.length">({{ moyskladProducts.length }})</span></h3>
            <div class="products-table-wrap" v-if="moyskladProducts.length">
              <table class="products-table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Кол-во</th>
                    <th>Цена</th>
                    <th>Сумма</th>
                    <th>ExternalCode</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in moyskladProducts" :key="'mp-'+i">
                    <td>{{ p.name }}</td>
                    <td>{{ p.quantity }}</td>
                    <td>{{ p.price }}</td>
                    <td>{{ p.sum }}</td>
                    <td>{{ p.externalCode || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-data">Нет товаров</div>
          </div>

          <!-- Сопоставление товаров -->
          <div class="modal-section" v-if="matchedProducts.length">
            <h3>Сопоставление товаров</h3>
            <div class="products-table-wrap">
              <table class="products-table matched-table">
                <thead>
                  <tr>
                    <th>Товар</th>
                    <th>Статус</th>
                    <th>Кол-во CRM</th>
                    <th>Кол-во МС</th>
                    <th>Цена CRM</th>
                    <th>Цена МС</th>
                    <th>Сумма CRM</th>
                    <th>Сумма МС</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in matchedProducts" :key="'match-'+i" :class="matchStatusClass(p.status)">
                    <td>{{ p.name }}</td>
                    <td>
                      <span :class="['match-status', matchStatusClass(p.status)]">{{ matchStatusText(p.status) }}</span>
                    </td>
                    <td>{{ p.retail ? p.retail.quantity : '—' }}</td>
                    <td>{{ p.moysklad ? p.moysklad.quantity : '—' }}</td>
                    <td>{{ p.retail ? p.retail.price : '—' }}</td>
                    <td>{{ p.moysklad ? p.moysklad.price : '—' }}</td>
                    <td>{{ p.retail ? p.retail.sum : '—' }}</td>
                    <td>{{ p.moysklad ? p.moysklad.sum : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Документы отгрузок -->
          <div class="modal-section" v-if="demandDocuments.length">
            <h3>Отгрузки <span class="count-badge">({{ demandDocuments.length }})</span></h3>
            <div v-for="(doc, di) in demandDocuments" :key="'demdoc-'+di" class="document-block">
              <div class="document-header">
                <span class="document-name">{{ doc.name || 'Отгрузка ' + (di + 1) }}</span>
                <span class="document-date" v-if="doc.moment">{{ formatMoment(doc.moment) }}</span>
                <span class="document-sum">Сумма: {{ doc.sum }} BYN</span>
              </div>
              <div class="products-table-wrap" v-if="doc.positions && doc.positions.length">
                <table class="products-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Кол-во</th>
                      <th>Цена</th>
                      <th>Сумма</th>
                      <th>ExternalCode</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, pi) in doc.positions" :key="'dem-'+di+'-'+pi">
                      <td>{{ p.name }}</td>
                      <td>{{ p.quantity }}</td>
                      <td>{{ p.price }}</td>
                      <td>{{ p.sum }}</td>
                      <td>{{ p.externalCode || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Документы возвратов -->
          <div class="modal-section" v-if="returnDocuments.length">
            <h3>Возвраты <span class="count-badge">({{ returnDocuments.length }})</span></h3>
            <div v-for="(doc, di) in returnDocuments" :key="'retdoc-'+di" class="document-block return-block">
              <div class="document-header">
                <span class="document-name">{{ doc.name || 'Возврат ' + (di + 1) }}</span>
                <span class="document-date" v-if="doc.moment">{{ formatMoment(doc.moment) }}</span>
                <span class="document-sum">Сумма: {{ doc.sum }} BYN</span>
              </div>
              <div class="products-table-wrap" v-if="doc.positions && doc.positions.length">
                <table class="products-table return-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Кол-во</th>
                      <th>Цена</th>
                      <th>Сумма</th>
                      <th>ExternalCode</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, pi) in doc.positions" :key="'ret-'+di+'-'+pi">
                      <td>{{ p.name }}</td>
                      <td>{{ p.quantity }}</td>
                      <td>{{ p.price }}</td>
                      <td>{{ p.sum }}</td>
                      <td>{{ p.externalCode || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Документы розничных продаж (привязанных к заказу покупателя) -->
          <div class="modal-section" v-if="retailDemandDocuments.length">
            <h3>Розничные продажи <span class="count-badge">({{ retailDemandDocuments.length }})</span></h3>
            <div v-for="(doc, di) in retailDemandDocuments" :key="'rddemdoc-'+di" class="document-block retail-demand-block">
              <div class="document-header">
                <span class="document-name">{{ doc.name || 'Розничная продажа ' + (di + 1) }}</span>
                <span class="document-date" v-if="doc.moment">{{ formatMoment(doc.moment) }}</span>
                <span class="document-sum">Сумма: {{ doc.sum }} BYN</span>
              </div>
              <div class="products-table-wrap" v-if="doc.positions && doc.positions.length">
                <table class="products-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Кол-во</th>
                      <th>Цена</th>
                      <th>Сумма</th>
                      <th>ExternalCode</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, pi) in doc.positions" :key="'rddem-'+di+'-'+pi">
                      <td>{{ p.name }}</td>
                      <td>{{ p.quantity }}</td>
                      <td>{{ p.price }}</td>
                      <td>{{ p.sum }}</td>
                      <td>{{ p.externalCode || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Документы розничных возвратов -->
          <div class="modal-section" v-if="retailReturnDocuments.length">
            <h3>Розничные возвраты <span class="count-badge">({{ retailReturnDocuments.length }})</span></h3>
            <div v-for="(doc, di) in retailReturnDocuments" :key="'rretdoc-'+di" class="document-block return-block">
              <div class="document-header">
                <span class="document-name">{{ doc.name || 'Розничный возврат ' + (di + 1) }}</span>
                <span class="document-date" v-if="doc.moment">{{ formatMoment(doc.moment) }}</span>
                <span class="document-sum">Сумма: {{ doc.sum }} BYN</span>
              </div>
              <div class="products-table-wrap" v-if="doc.positions && doc.positions.length">
                <table class="products-table return-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Кол-во</th>
                      <th>Цена</th>
                      <th>Сумма</th>
                      <th>ExternalCode</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, pi) in doc.positions" :key="'rret-'+di+'-'+pi">
                      <td>{{ p.name }}</td>
                      <td>{{ p.quantity }}</td>
                      <td>{{ p.price }}</td>
                      <td>{{ p.sum }}</td>
                      <td>{{ p.externalCode || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Расхождения -->
          <div class="modal-section" v-if="modalData.details && modalData.details.length">
            <h3>Расхождения</h3>
            <ul class="differences-list">
              <li v-for="(d, i) in (Array.isArray(modalData.details) ? modalData.details : [modalData.details])" :key="'diff-'+i">
                {{ d }}
              </li>
            </ul>
          </div>

          <!-- Ссылки -->
          <div class="modal-section">
            <h3>Ссылки</h3>
            <div class="links-row">
              <a 
                :href="retailOrderUrl(modalData.retail_order_id || modalData.id)" 
                target="_blank" 
                class="external-link"
              >
                Открыть в RetailCRM →
              </a>
              <a 
                v-if="modalData.moysklad_uuid"
                :href="'https://online.moysklad.ru/app/#customerorder/edit?id=' + modalData.moysklad_uuid" 
                target="_blank" 
                class="external-link"
              >
                Открыть в МойСклад →
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const PRODUCT_STATUS_MAP = {
  'new': 'Зарезервирован МС',
  'ne-otgruzhen-eshche': 'Не отгружен еще',
  'prodan': 'Доставляется курьером',
  'assembly-not-delivery': 'Доставляется ТК',
  'completed': 'Укомплектован',
  'product-is-damaged': 'Укомплектован',
  'saled': 'Продан в магазине',
  'prodan-1': 'Продан (контроль)',
  'returned': 'Возврат',
  'ukomplektovan': 'Укомплектован',
  'ukomplektovan-1': 'Укомплектован',
  'ukomplektovan-2': 'Отмена',
  'in-reserve': 'Отмена',
  'confirming': 'Отмена с отгрузкой ТК',
  'assembly-delivery': 'Отмена с отгрузкой курьеру',
  'failure': 'Возврат',
  'out-of-stock': 'Отмена заказ из магазина',
  'lost': 'Отмена с отгрузкой на складе',
  'e': 'Отмена Купил в другом месте',
  'otmena-ne-ustroila-tsena': 'Отмена Не устроила цена',
  '1': 'Снять с резерва',
  'sniat-s-rezerva': 'Снять с резерва',
  'sniat-s-rezerva-1': 'Снять с резерва',
  'sniat-s-rezerva-2': 'Снять с резерва',
  'sniat-s-rezerva-3': 'Снять с резерва',
  'sniat-s-rezerva-4': 'Снять с резерва',
  'sniat-s-rezerva-5': 'Снять с резерва',
  'sniat-s-rezerva-6': 'Снять с резерва',
  'sniat-s-rezerva-7': 'Снять с резерва',
  'sniat-s-rezerva-8': 'Снять с резерва',
  'sniat-s-rezerva-9': 'Снять с резерва',
  'sniat-s-rezerva-10': 'Снять с резерва'
};

/** id совпадают с webhook_server.js RETAILCRM_REPORT_SERVICE_PRODUCT_FILTERS */
const SERVICE_PRODUCT_FILTER_OPTIONS = [
  { id: 'delivery_989773504', label: 'Доставка (989773504)' },
  { id: 'complex1_989773501', label: 'Комплекс 1 (989773501)' },
  { id: 'complex2_989773505', label: 'Комплекс 2 (989773505)' },
  { id: 'complex3_989773503', label: 'Комплекс 3 (989773503)' },
  { id: 'svc_flat_delivery', label: 'Услуга «доставка до квартиры АКБ»' },
  { id: 'svc_exchange', label: 'Обмен товара' },
  { id: 'svc_return_pickup', label: 'Забрать у клиента возвратный товар' },
  { id: 'svc_current_measure', label: 'Измерение тока утечки' },
  { id: 'svc_battery_install', label: 'Помощь в установке аккумулятора' },
  { id: 'svc_diag', label: 'Диагностика АКБ, генератора, стартера' }
];

const PRODUCT_STATUS_CLASSES = {
  'new': 'status-new',
  'ne-otgruzhen-eshche': 'status-pending',
  'prodan': 'status-delivery',
  'assembly-not-delivery': 'status-delivery',
  'completed': 'status-completed',
  'product-is-damaged': 'status-completed',
  'saled': 'status-sold',
  'prodan-1': 'status-sold',
  'returned': 'status-returned',
  'failure': 'status-returned',
  'ukomplektovan': 'status-completed',
  'ukomplektovan-1': 'status-completed',
  'ukomplektovan-2': 'status-canceled',
  'in-reserve': 'status-canceled',
  'confirming': 'status-canceled',
  'assembly-delivery': 'status-canceled',
  'out-of-stock': 'status-canceled',
  'lost': 'status-canceled',
  'e': 'status-canceled',
  'otmena-ne-ustroila-tsena': 'status-canceled',
  '1': 'status-canceled',
  'sniat-s-rezerva': 'status-canceled',
  'sniat-s-rezerva-1': 'status-canceled',
  'sniat-s-rezerva-2': 'status-canceled',
  'sniat-s-rezerva-3': 'status-canceled',
  'sniat-s-rezerva-4': 'status-canceled',
  'sniat-s-rezerva-5': 'status-canceled',
  'sniat-s-rezerva-6': 'status-canceled',
  'sniat-s-rezerva-7': 'status-canceled',
  'sniat-s-rezerva-8': 'status-canceled',
  'sniat-s-rezerva-9': 'status-canceled',
  'sniat-s-rezerva-10': 'status-canceled'
};

export default {
  name: 'RetailCrmMoyskladReport',
  data() {
    return {
      reports: [],
      loadingList: true,
      selectedPeriods: [],
      openDropdown: '',
      searchQuery: '',
      statusFilter: null,
      groupFilter: '',
      demandFilter: '',
      returnFilter: '',
      retailReturnFilter: '',
      retailStatusFilter: [],
      moyskladStatusFilter: [],
      retailStoreFilter: [],
      moyskladStoreFilter: [],
      matchStatusFilter: '',
      matchProductsFilter: '',
      matchStoresFilter: '',
      matchGroupFilter: '',
      uuidFilter: '',
      serviceProductFilter: [],
      sortBy: '',
      sortDir: 'asc',
      filterOptions: {
        retail_statuses: [],
        moysklad_statuses: [],
        retail_stores: [],
        moysklad_stores: [],
        service_products: []
      },
      loading: false,
      orders: [],
      reportMeta: {},
      reportData: {
        page: 1,
        per_page: 100,
        total_filtered: 0,
        total_pages: 0
      },
      perPage: 100,
      modalOpen: false,
      modalData: {},
      // Массовое обновление
      selectedOrders: [],
      bulkFieldValue: '',
      bulkFieldName: 'пометка заказа',
      bulkUpdating: false,
      bulkUpdateResult: null,
      selectedForRecompare: [],
      selectAllByFilterLoading: false,
      recompareLoading: false,
      recompareResult: null,
      verifiedSaving: null
    };
  },
  computed: {
    hasAnyFilter() {
      return this.searchQuery || this.statusFilter || this.groupFilter || 
             this.demandFilter || this.returnFilter || this.retailReturnFilter ||
             this.uuidFilter || this.sortBy ||
             this.retailStatusFilter.length || this.moyskladStatusFilter.length || 
             this.retailStoreFilter.length || this.moyskladStoreFilter.length ||
             this.matchStatusFilter || this.matchProductsFilter || 
             this.matchStoresFilter || this.matchGroupFilter ||
             this.serviceProductFilter.length;
    },
    serviceProductOptions() {
      return (this.filterOptions.service_products && this.filterOptions.service_products.length)
        ? this.filterOptions.service_products
        : SERVICE_PRODUCT_FILTER_OPTIONS;
    },
    allVisibleSelected() {
      if (!this.orders.length) return false;
      const validOrders = this.orders.filter(o => o.moysklad_uuid);
      if (!validOrders.length) return false;
      return validOrders.every(o => this.selectedOrders.some(s => s.moysklad_uuid === o.moysklad_uuid));
    },
    compareResult() {
      return this.modalData.compare_result || null;
    },
    retailProducts() {
      if (this.compareResult && this.compareResult.products && this.compareResult.products.retail_products) {
        return this.compareResult.products.retail_products;
      }
      return [];
    },
    moyskladProducts() {
      if (this.compareResult && this.compareResult.products && this.compareResult.products.moysklad_products) {
        return this.compareResult.products.moysklad_products;
      }
      return [];
    },
    matchedProducts() {
      if (this.compareResult && this.compareResult.products && this.compareResult.products.matched_products) {
        return this.compareResult.products.matched_products;
      }
      return [];
    },
    demandDocuments() {
      if (this.compareResult && this.compareResult.demand_documents) {
        return this.compareResult.demand_documents;
      }
      return [];
    },
    returnDocuments() {
      if (this.compareResult && this.compareResult.return_documents) {
        return this.compareResult.return_documents;
      }
      return [];
    },
    retailReturnDocuments() {
      if (this.compareResult && this.compareResult.retail_return_documents) {
        return this.compareResult.retail_return_documents;
      }
      return [];
    },
    retailDemandDocuments() {
      if (this.compareResult && this.compareResult.retail_demand_documents) {
        return this.compareResult.retail_demand_documents;
      }
      return [];
    }
  },
  watch: {
    perPage(v) {
      this.reportData.per_page = v;
    }
  },
  mounted() {
    this.fetchReportList();
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.closeDropdownClick);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.closeDropdownClick);
  },
  methods: {
    handleKeydown(e) {
      if (e.key === 'Escape' && this.modalOpen) {
        this.closeModal();
      } else if (e.key === 'Escape') {
        this.openDropdown = '';
      }
    },
    closeDropdownClick(e) {
      if (e.target.closest && e.target.closest('.multiselect-wrap')) return;
      this.openDropdown = '';
    },
    formatDate(ms) {
      if (!ms) return '';
      const d = new Date(ms);
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    retailOrderUrl(id) {
      if (!id) return '#';
      return `https://1akb.retailcrm.ru/orders/${id}/edit`;
    },
    moyskladOrderUrl(uuid) {
      if (!uuid) return '#';
      return `https://online.moysklad.ru/app/#customerorder/edit?id=${uuid}`;
    },
    formatMoment(moment) {
      if (!moment) return '';
      const d = new Date(moment);
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    statusClass(status) {
      if (status === 'соответствует') return 'status-ok';
      if (status === 'не найдено в МС') return 'status-warn';
      if (status === 'не соответствует') return 'status-err';
      return '';
    },
    /** Позиции заказа RetailCRM из compare_result */
    getRetailProductLines(row) {
      const cr = row && row.compare_result;
      const list = cr && cr.products && Array.isArray(cr.products.retail_products)
        ? cr.products.retail_products
        : [];
      return list;
    },
    mergePositionDocuments(docs) {
      if (!docs || !docs.length) return [];
      const map = new Map();
      for (const doc of docs) {
        const positions = doc && Array.isArray(doc.positions) ? doc.positions : [];
        for (const p of positions) {
          const key = String(p.externalCode || p.id || p.name || '').trim() || JSON.stringify([p.name, p.article]);
          const prev = map.get(key);
          const qty = Number(p.quantity) || 0;
          if (!prev) {
            map.set(key, { ...p, quantity: qty });
          } else {
            prev.quantity = (Number(prev.quantity) || 0) + qty;
          }
        }
      }
      return Array.from(map.values());
    },
    /** Розничные продажи в МС (retaildemand), в т.ч. привязанные к заказу покупателя */
    getMergedRetailDemandPositions(row) {
      const cr = row && row.compare_result;
      if (cr && Array.isArray(cr.retail_demand_positions) && cr.retail_demand_positions.length) {
        return cr.retail_demand_positions;
      }
      const docs = (cr && Array.isArray(cr.retail_demand_documents)) ? cr.retail_demand_documents : [];
      return this.mergePositionDocuments(docs);
    },
    /** Сводные позиции отгрузки МС (заказ покупателя): demand_positions или demand_documents */
    getMergedDemandPositions(row) {
      const cr = row && row.compare_result;
      const fromFlat = (cr && Array.isArray(cr.demand_positions) && cr.demand_positions.length)
        ? cr.demand_positions
        : (row && Array.isArray(row.demand_positions) && row.demand_positions.length ? row.demand_positions : null);
      if (fromFlat && fromFlat.length) return fromFlat;
      const docs = (cr && Array.isArray(cr.demand_documents) ? cr.demand_documents : []);
      return this.mergePositionDocuments(docs);
    },
    /** Колонка «Отгрузки»: сначала розничная продажа в МС, затем отгрузки customerorder, затем состав заказа МС из сверки */
    getMoyskladCompositionLines(row) {
      const retail = this.getMergedRetailDemandPositions(row);
      if (retail.length) return retail;
      const demand = this.getMergedDemandPositions(row);
      if (demand.length) return demand;
      const cr = row && row.compare_result;
      const mp = cr && cr.products && Array.isArray(cr.products.moysklad_products) ? cr.products.moysklad_products : [];
      return mp;
    },
    matchStatusClass(status) {
      if (status === 'match') return 'matched';
      if (status === 'missing_in_moysklad') return 'missing-ms';
      if (status === 'missing_in_retail') return 'missing-crm';
      return 'mismatch';
    },
    matchStatusText(status) {
      if (status === 'match') return 'Совпадает';
      if (status === 'missing_in_moysklad') return 'Нет в МС';
      if (status === 'missing_in_retail') return 'Нет в CRM';
      return 'Расхождение';
    },
    getProductStatusLabel(status) {
      return PRODUCT_STATUS_MAP[status] || status || '—';
    },
    getProductStatusClass(status) {
      return PRODUCT_STATUS_CLASSES[status] || 'status-unknown';
    },
    hasMovement(type) {
      const key = 'has_' + type;
      // Сначала проверяем в compare_result, потом в modalData напрямую
      if (this.compareResult && this.compareResult[key] !== undefined) {
        return this.compareResult[key];
      }
      if (this.modalData && this.modalData[key] !== undefined) {
        return this.modalData[key];
      }
      return false;
    },
    openModal(row) {
      this.modalData = row;
      this.modalOpen = true;
      document.body.style.overflow = 'hidden';
    },
    closeModal() {
      this.modalOpen = false;
      this.modalData = {};
      document.body.style.overflow = '';
    },
    // Методы для массового выбора
    isSelected(row) {
      return this.selectedOrders.some(o => o.moysklad_uuid === row.moysklad_uuid);
    },
    toggleSelect(row) {
      if (!row.moysklad_uuid) return;
      const idx = this.selectedOrders.findIndex(o => o.moysklad_uuid === row.moysklad_uuid);
      if (idx >= 0) {
        this.selectedOrders.splice(idx, 1);
      } else {
        this.selectedOrders.push({
          moysklad_uuid: row.moysklad_uuid,
          number: row.number,
          retail_order_id: row.retail_order_id
        });
      }
      this.bulkUpdateResult = null;
    },
    toggleSelectAll() {
      const validOrders = this.orders.filter(o => o.moysklad_uuid);
      if (this.allVisibleSelected) {
        // Снять выделение с видимых
        validOrders.forEach(o => {
          const idx = this.selectedOrders.findIndex(s => s.moysklad_uuid === o.moysklad_uuid);
          if (idx >= 0) this.selectedOrders.splice(idx, 1);
        });
      } else {
        // Добавить видимые
        validOrders.forEach(o => {
          if (!this.selectedOrders.some(s => s.moysklad_uuid === o.moysklad_uuid)) {
            this.selectedOrders.push({
              moysklad_uuid: o.moysklad_uuid,
              number: o.number,
              retail_order_id: o.retail_order_id
            });
          }
        });
      }
      this.bulkUpdateResult = null;
    },
    clearSelection() {
      this.selectedOrders = [];
      this.bulkUpdateResult = null;
    },
    async selectAllByFilter() {
      if (!this.selectedPeriods.length) return;
      this.selectAllByFilterLoading = true;
      this.recompareResult = null;
      try {
        const { data } = await axios.get('/retailcrm-report-data', {
          params: {
            period: this.selectedPeriods.join(','),
            ids_only: '1',
            search: this.searchQuery || undefined,
            status: this.statusFilter || undefined,
            group: this.groupFilter || undefined,
            demand: this.demandFilter || undefined,
            return: this.returnFilter || undefined,
            retail_return: this.retailReturnFilter || undefined,
            retail_status: this.retailStatusFilter.length ? this.retailStatusFilter.join('|') : undefined,
            moysklad_status: this.moyskladStatusFilter.length ? this.moyskladStatusFilter.join('|') : undefined,
            retail_store: this.retailStoreFilter.length ? this.retailStoreFilter.join('|') : undefined,
            moysklad_store: this.moyskladStoreFilter.length ? this.moyskladStoreFilter.join('|') : undefined,
            match_status: this.matchStatusFilter || undefined,
            match_products: this.matchProductsFilter || undefined,
            match_stores: this.matchStoresFilter || undefined,
            match_group: this.matchGroupFilter || undefined,
            uuid: this.uuidFilter || undefined,
            service_product: this.serviceProductFilter.length ? this.serviceProductFilter.join('|') : undefined
          }
        });
        if (data.success && Array.isArray(data.order_items)) {
          this.selectedForRecompare = data.order_items;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.selectAllByFilterLoading = false;
      }
    },
    clearRecompareSelection() {
      this.selectedForRecompare = [];
      this.recompareResult = null;
    },
    async setVerified(row, checked) {
      const period = row._period || (this.reportMeta && this.reportMeta.period) || '';
      if (!period || row.id == null) return;
      this.verifiedSaving = row.id;
      try {
        const { data } = await axios.post('/retailcrm-report-set-verified', {
          period,
          order_id: row.id,
          verified: checked
        });
        if (data.success) {
          this.$set(row, 'verified', !!checked);
        }
      } catch (e) {
        // оставляем галочку как была
      } finally {
        this.verifiedSaving = null;
      }
    },
    async runRecompare() {
      if (!this.selectedForRecompare.length) return;
      this.recompareLoading = true;
      this.recompareResult = null;
      const batchSize = 2;
      const total = this.selectedForRecompare.length;
      let totalUpdated = 0;
      const errors = [];
      const failedBatches = [];
      for (let i = 0; i < total; i += batchSize) {
        const batch = this.selectedForRecompare.slice(i, i + batchSize);
        this.recompareResult = { success: true, message: `Пересравнение ${Math.min(i + batchSize, total)} из ${total}...`, updated: totalUpdated };
        try {
          const { data } = await axios.post('/retailcrm-report-recompare', {
            order_items: batch
          }, { timeout: 120000 });
          if (data.success && data.updated != null) totalUpdated += data.updated;
          if (data.errors && data.errors.length) errors.push(...data.errors);
        } catch (e) {
          failedBatches.push(batch);
          const msg = (e.response && e.response.status === 504) ? 'Таймаут 504' : ((e.response && e.response.data && e.response.data.message) ? e.response.data.message : (e.message || 'Ошибка'));
          errors.push(`пачка ${Math.floor(i / batchSize) + 1}: ${msg}`);
        }
      }
      this.recompareLoading = false;
      const failedItems = failedBatches.flat();
      const failedCount = failedItems.length;
      if (failedBatches.length > 0) {
        this.selectedForRecompare = failedItems;
        this.recompareResult = {
          success: totalUpdated > 0,
          message: `Обновлено: ${totalUpdated}. Не удалось: ${failedCount} (остались в списке — нажмите «Пересравнить» снова). ${errors.length ? errors.slice(-3).join('; ') : ''}`,
          updated: totalUpdated
        };
      } else {
        this.selectedForRecompare = [];
        this.recompareResult = {
          success: errors.length === 0,
          message: errors.length ? `Обновлено: ${totalUpdated}. Ошибки: ${errors.join('; ')}` : (totalUpdated > 0 ? `Готово. Обновлено записей: ${totalUpdated}` : 'Сервер не обновил ни одной строки в JSON (проверьте period/id в ответе errors или пересоберите отчёт).'),
          updated: totalUpdated
        };
      }
      // Раньше loadData вызывался только при totalUpdated > 0 — при «0 обновлений» таблица не перезагружалась и казалось, что кнопка ничего не делает.
      this.loadData(this.reportData.page || 1);
    },
    async bulkUpdateField() {
      if (!this.selectedOrders.length || !this.bulkFieldValue.trim()) return;
      
      this.bulkUpdating = true;
      this.bulkUpdateResult = null;
      
      try {
        const { data } = await axios.post('/moysklad-bulk-update', {
          orders: this.selectedOrders.map(o => o.moysklad_uuid),
          field_value: this.bulkFieldValue.trim()
        });
        
        if (data.success) {
          this.bulkUpdateResult = {
            success: true,
            message: `Успешно обновлено ${data.updated} из ${data.total} заказов`
          };
          this.bulkFieldValue = '';
          this.selectedOrders = [];
        } else {
          this.bulkUpdateResult = {
            success: false,
            message: data.message || 'Ошибка обновления'
          };
        }
      } catch (e) {
        this.bulkUpdateResult = {
          success: false,
          message: (e.response && e.response.data && e.response.data.message) || e.message || 'Ошибка запроса'
        };
      } finally {
        this.bulkUpdating = false;
      }
    },
    async fetchReportList() {
      this.loadingList = true;
      try {
        const { data } = await axios.get('/retailcrm-report-list');
        if (data.success && data.reports && data.reports.length) {
          this.reports = data.reports;
          if (!this.selectedPeriods.length && data.reports[0]) {
            this.selectedPeriods = [data.reports[0].period];
            this.$nextTick(() => this.loadData(1));
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingList = false;
      }
    },
    togglePeriod(period) {
      const i = this.selectedPeriods.indexOf(period);
      if (i === -1) this.selectedPeriods.push(period);
      else this.selectedPeriods.splice(i, 1);
      this.loadData(1);
    },
    toggleAllPeriods() {
      if (this.selectedPeriods.length === this.reports.length) {
        this.selectedPeriods = [];
      } else {
        this.selectedPeriods = this.reports.map(r => r.period);
      }
      this.loadData(1);
    },
    toggleServiceProductFilter(id) {
      const arr = this.serviceProductFilter.slice();
      const i = arr.indexOf(id);
      if (i === -1) arr.push(id);
      else arr.splice(i, 1);
      this.serviceProductFilter = arr;
      this.loadData(1);
    },
    toggleFilterMulti(field, value) {
      const key = { retail_status: 'retailStatusFilter', moysklad_status: 'moyskladStatusFilter', retail_store: 'retailStoreFilter', moysklad_store: 'moyskladStoreFilter' }[field] || field;
      const arr = (this[key] || []).slice();
      const i = arr.indexOf(value);
      if (i === -1) arr.push(value);
      else arr.splice(i, 1);
      this[key] = arr;
      this.loadData(1);
    },
    toggleStatusFilter(status) {
      this.statusFilter = this.statusFilter === status ? null : status;
      this.loadData(1);
    },
    clearSearch() {
      this.searchQuery = '';
      this.loadData(1);
    },
    clearFilters() {
      this.searchQuery = '';
      this.statusFilter = null;
      this.groupFilter = '';
      this.demandFilter = '';
      this.returnFilter = '';
      this.retailReturnFilter = '';
      this.retailStatusFilter = [];
      this.moyskladStatusFilter = [];
      this.retailStoreFilter = [];
      this.moyskladStoreFilter = [];
      this.matchStatusFilter = '';
      this.matchProductsFilter = '';
      this.matchStoresFilter = '';
      this.matchGroupFilter = '';
      this.uuidFilter = '';
      this.serviceProductFilter = [];
      this.sortBy = '';
      this.sortDir = 'asc';
      this.loadData(1);
    },
    setSort(field) {
      if (this.sortBy === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortDir = 'asc';
      }
      this.loadData(1);
    },
    async loadData(page) {
      if (!this.selectedPeriods.length) return;
      this.loading = true;
      try {
        const { data } = await axios.get('/retailcrm-report-data', {
          params: {
            period: this.selectedPeriods.join(','),
            page: page || this.reportData.page,
            per_page: this.perPage,
            search: this.searchQuery || undefined,
            status: this.statusFilter || undefined,
            group: this.groupFilter || undefined,
            demand: this.demandFilter || undefined,
            return: this.returnFilter || undefined,
            retail_return: this.retailReturnFilter || undefined,
            retail_status: this.retailStatusFilter.length ? this.retailStatusFilter.join('|') : undefined,
            moysklad_status: this.moyskladStatusFilter.length ? this.moyskladStatusFilter.join('|') : undefined,
            retail_store: this.retailStoreFilter.length ? this.retailStoreFilter.join('|') : undefined,
            moysklad_store: this.moyskladStoreFilter.length ? this.moyskladStoreFilter.join('|') : undefined,
            match_status: this.matchStatusFilter || undefined,
            match_products: this.matchProductsFilter || undefined,
            match_stores: this.matchStoresFilter || undefined,
            match_group: this.matchGroupFilter || undefined,
            uuid: this.uuidFilter || undefined,
            service_product: this.serviceProductFilter.length ? this.serviceProductFilter.join('|') : undefined,
            sort_by: this.sortBy || undefined,
            sort_dir: this.sortDir
          }
        });
        if (data.success) {
          this.orders = data.orders || [];
          this.reportMeta = {
            period: data.period,
            period_label: data.period_label,
            generated_at: data.generated_at,
            total_orders: data.total_orders,
            summary: data.summary || {}
          };
          this.reportData = {
            page: data.page,
            per_page: data.per_page,
            total_filtered: data.total_filtered,
            total_pages: data.total_pages
          };
          if (data.filters) {
            this.filterOptions = {
              retail_statuses: data.filters.retail_statuses || [],
              moysklad_statuses: data.filters.moysklad_statuses || [],
              retail_stores: data.filters.retail_stores || [],
              moysklad_stores: data.filters.moysklad_stores || [],
              service_products: data.filters.service_products || []
            };
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.retailcrm-report {
  padding: 16px;
  max-width: 100%;
}
.header-main {
  margin-bottom: 20px;
}
.report-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}
.filter-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  min-width: 200px;
}
.filter-section-main {
  flex-direction: row;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  background: #e7f1ff;
  border-color: #b6d4fe;
}
.filter-section-actions {
  flex-direction: row;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  padding: 0;
  min-width: auto;
}
.filter-section-title {
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 4px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e9ecef;
}
.multiselect-wrap {
  position: relative;
}
.multiselect-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 180px;
  max-width: 280px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 0.9em;
  user-select: none;
}
.multiselect-trigger:hover {
  border-color: #adb5bd;
  background: #f8f9fa;
}
.multiselect-trigger.open {
  border-color: #0d6efd;
  outline: 1px solid rgba(13, 110, 253, 0.25);
}
.multiselect-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.multiselect-arrow {
  margin-left: 8px;
  color: #6c757d;
  font-size: 0.8em;
}
.multiselect-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 220px;
  max-height: 280px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  padding: 6px 0;
}
.multiselect-dropdown-periods {
  min-width: 260px;
}
.multiselect-dropdown-list {
  max-height: 220px;
}
.multiselect-dropdown-wide {
  min-width: 320px;
  max-width: min(560px, 96vw);
  max-height: 320px;
}
.filter-section-wide {
  flex: 1 1 100%;
  max-width: 100%;
}
.multiselect-item-text {
  white-space: normal;
  align-items: flex-start;
}
.multiselect-item-text span {
  line-height: 1.35;
  font-size: 12px;
}
.multiselect-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.9em;
  white-space: nowrap;
}
.multiselect-item:hover {
  background: #f1f3f5;
}
.multiselect-item input {
  margin: 0;
  flex-shrink: 0;
}
.multiselect-select-all {
  border-bottom: 1px solid #eee;
  font-weight: 500;
}
.multiselect-loading,
.multiselect-empty {
  padding: 12px;
  color: #6c757d;
  font-size: 0.9em;
  margin: 0;
}
.filter-grid {
  display: grid;
  gap: 10px;
}
.filter-grid-2 {
  grid-template-columns: repeat(2, 1fr);
}
.filter-grid-4 {
  grid-template-columns: repeat(4, 1fr);
}
.filter-grid-5 {
  grid-template-columns: repeat(5, 1fr);
}
.filter-grid-6 {
  grid-template-columns: repeat(6, 1fr);
}
@media (max-width: 1200px) {
  .filter-grid-4 {
    grid-template-columns: repeat(2, 1fr);
  }
  .filter-grid-5,
  .filter-grid-6 {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 768px) {
  .filter-grid-2,
  .filter-grid-4,
  .filter-grid-5,
  .filter-grid-6 {
    grid-template-columns: 1fr;
  }
  .filter-section-main {
    flex-direction: column;
    align-items: stretch;
  }
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filter-label {
  font-size: 0.8em;
  font-weight: 500;
  color: #495057;
  white-space: nowrap;
}
.filter-select {
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  font-size: 0.9em;
  min-width: 100px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.filter-select:hover {
  border-color: #86b7fe;
}
.filter-select:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}
.filter-select-wide {
  min-width: 180px;
}
.search-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.search-input {
  width: 160px;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9em;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.search-input:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}
.btn-clear-search {
  width: 32px;
  height: 34px;
  padding: 0;
  line-height: 1;
  font-size: 18px;
  color: #6c757d;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-clear-search:hover {
  color: #fff;
  background: #dc3545;
  border-color: #dc3545;
}
.btn-search {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #0d6efd, #0b5ed7);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.3);
}
.btn-search:hover {
  background: linear-gradient(135deg, #0b5ed7, #0a58ca);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.4);
  transform: translateY(-1px);
}
.btn-search:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.3);
}
.btn-icon {
  font-size: 1em;
}
.btn-clear {
  padding: 10px 16px;
  background: #fff;
  color: #6c757d;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9em;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-clear:hover {
  color: #dc3545;
  border-color: #dc3545;
  background: #fff5f5;
}
.loading-label {
  color: #6c757d;
  font-size: 0.85em;
  font-style: italic;
}
.report-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 0.95em;
}
.summary-item { margin-right: 8px; }
.summary-click {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
}
.summary-click:hover { opacity: 0.85; }
.summary-active {
  font-weight: 700;
  text-decoration-style: solid;
}
.summary-ok { color: #198754; }
.summary-warn { color: #fd7e14; }
.summary-err { color: #dc3545; }
.btn-clear {
  padding: 8px 12px;
  background: #fff;
  color: #6c757d;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95em;
}
.btn-clear:hover {
  background: #f8f9fa;
  color: #495057;
}
.badge-group {
  display: inline-block;
  padding: 4px 8px;
  background-color: #6f42c1;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}
.badge-move {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  margin-right: 4px;
  font-weight: bold;
}
.move-yes {
  background-color: #d1e7dd;
  color: #0f5132;
}
.move-no {
  background-color: #f8d7da;
  color: #842029;
}
.sum-diff {
  color: #dc3545;
  font-weight: 500;
}
.match-yes {
  color: #198754;
  font-weight: 600;
  margin-right: 6px;
}
.match-no {
  color: #dc3545;
  font-weight: 600;
  margin-right: 6px;
}
.store-info {
  margin-bottom: 2px;
  font-size: 0.85em;
}
.filter-select {
  min-width: 150px;
  max-width: 220px;
  padding: 6px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9em;
}
.table-wrap {
  overflow-x: auto;
  margin-bottom: 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.report-table th,
.report-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}
.report-table th {
  background: #e9ecef;
  font-weight: 600;
  white-space: nowrap;
}
.th-sortable {
  cursor: pointer;
  user-select: none;
}
.th-sortable:hover {
  background: #dee2e6;
}
.th-sortable.sorted {
  background: #ced4da;
}
.sort-arrow {
  margin-left: 4px;
  font-size: 0.9em;
  color: #495057;
}
.report-table tbody tr:hover {
  background: #e3f2fd;
}
.clickable-row {
  cursor: pointer;
}
.cell-number { white-space: nowrap; font-weight: 500; }
.th-composition {
  white-space: normal;
  font-size: 11px;
  line-height: 1.25;
  font-weight: 600;
}
.cell-composition {
  min-width: 200px;
  font-size: 10px;
  line-height: 1.35;
  word-break: break-word;
  vertical-align: top;
  color: #212529;
}
.cell-composition .composition-line {
  font-size: 10px;
  line-height: 1.4;
  margin-bottom: 4px;
  word-break: break-word;
}
.cell-composition .composition-line:last-child {
  margin-bottom: 0;
}
.cell-composition .composition-line-canceled {
  text-decoration: line-through;
  opacity: 0.65;
  color: #6c757d;
}
.cell-composition .composition-empty {
  color: #6c757d;
  font-style: italic;
  font-size: 10px;
}
.cell-status { white-space: nowrap; }
.status-link {
  color: #0d6efd;
  text-decoration: none;
}
.status-link:hover {
  text-decoration: underline;
}
.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
}
.status-ok { background: #d1e7dd; color: #0f5132; }
.status-warn { background: #fff3cd; color: #664d03; }
.status-err { background: #f8d7da; color: #842029; }
.btn-details {
  padding: 4px 10px;
  background: #6c757d;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}
.btn-details:hover {
  background: #5a6268;
}
.cell-loading,
.cell-empty {
  text-align: center;
  color: #6c757d;
  padding: 24px;
}
.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.pagination-info { margin-right: 8px; }
.pagination button {
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.pagination button:hover:not(:disabled) {
  background: #e9ecef;
}
.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.per-page-select {
  padding: 6px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
}
.hint { color: #6c757d; margin-top: 16px; }

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 1000px;
  width: 100%;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
  margin: 20px 0;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
}
.modal-header h2 {
  margin: 0;
  font-size: 1.3em;
}
.modal-close {
  width: 36px;
  height: 36px;
  border: none;
  background: none;
  font-size: 28px;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
}
.modal-close:hover {
  color: #dc3545;
}
.modal-body {
  padding: 20px;
}
.modal-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}
.modal-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}
.modal-section h3 {
  margin: 0 0 12px 0;
  font-size: 1.1em;
  color: #333;
}
.count-badge {
  font-size: 0.85em;
  color: #6c757d;
  font-weight: normal;
}
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
}
.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.info-label {
  font-size: 0.85em;
  color: #6c757d;
}
.info-value {
  font-weight: 500;
}
.compare-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 20px;
  align-items: start;
}
.compare-col {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}
.compare-col h4 {
  margin: 0 0 8px 0;
  font-size: 0.9em;
  color: #6c757d;
}
.compare-value {
  font-weight: 500;
  font-size: 0.95em;
}
.compare-detail {
  font-size: 0.8em;
  color: #6c757d;
  margin-top: 4px;
}
.compare-middle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  min-width: 80px;
}
.match-icon-yes {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #d1e7dd;
  color: #0f5132;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
}
.match-icon-no {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f8d7da;
  color: #842029;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
}
.compare-method {
  font-size: 0.75em;
  color: #6c757d;
  margin-top: 8px;
  text-align: center;
}
.movement-grid {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.movement-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.movement-label {
  color: #6c757d;
}
.movement-value {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
}
.validation-result {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 6px;
}
.validation-ok {
  background: #d1e7dd;
  color: #0f5132;
}
.validation-fail {
  background: #f8d7da;
  color: #842029;
}
.validation-icon {
  font-size: 1.5em;
  font-weight: bold;
}
.validation-message {
  font-weight: 500;
}
.sums-grid {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.sum-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sum-label {
  color: #6c757d;
}
.sum-value {
  font-weight: 600;
  font-size: 1.1em;
}
.no-data {
  color: #6c757d;
  font-style: italic;
}
.products-table-wrap {
  overflow-x: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}
.products-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;
}
.products-table th,
.products-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}
.products-table th {
  background: #e9ecef;
  font-weight: 600;
}
.products-table tbody tr:hover {
  background: #f8f9fa;
}
.return-table tbody tr {
  background: #fff8e6;
}
.return-table tbody tr:hover {
  background: #fff3cd;
}
.matched-table .matched {
  background: #d1e7dd;
}
.matched-table .missing-ms {
  background: #fff3cd;
}
.matched-table .missing-crm {
  background: #cff4fc;
}
.matched-table .mismatch {
  background: #f8d7da;
}
.match-status {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.85em;
  font-weight: 500;
}
.match-status.matched {
  background: #198754;
  color: #fff;
}
.match-status.missing-ms {
  background: #fd7e14;
  color: #fff;
}
.match-status.missing-crm {
  background: #0dcaf0;
  color: #000;
}
.match-status.mismatch {
  background: #dc3545;
  color: #fff;
}
.differences-list {
  margin: 0;
  padding-left: 20px;
  color: #842029;
}
.differences-list li {
  margin: 6px 0;
}
.links-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.external-link {
  display: inline-block;
  padding: 8px 16px;
  background: #0d6efd;
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.9em;
}
.external-link:hover {
  background: #0b5ed7;
}
.document-block {
  margin-bottom: 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}
.document-block:last-child {
  margin-bottom: 0;
}
.document-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  background: #e9ecef;
  font-size: 0.95em;
}
.document-name {
  font-weight: 600;
  color: #333;
}
.document-date {
  color: #6c757d;
}
.document-sum {
  margin-left: auto;
  font-weight: 500;
  color: #198754;
}
.return-block .document-header {
  background: #fff3cd;
}
.return-block .document-sum {
  color: #dc3545;
}
.retail-demand-block .document-header {
  background: #d1e7dd;
}
.retail-demand-block .document-sum {
  color: #0f5132;
}
.document-block .products-table-wrap {
  border: none;
  border-radius: 0;
}
/* Product status badges */
.product-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  white-space: nowrap;
}
.status-new {
  background: #cce5ff;
  color: #004085;
}
.status-pending {
  background: #fff3cd;
  color: #856404;
}
.status-delivery {
  background: #d1e7dd;
  color: #0f5132;
}
.status-completed {
  background: #d4edda;
  color: #155724;
}
.status-sold {
  background: #198754;
  color: #fff;
}
.status-returned {
  background: #f8d7da;
  color: #842029;
}
.status-canceled {
  background: #e2e3e5;
  color: #6c757d;
  text-decoration: line-through;
}
.status-unknown {
  background: #f8f9fa;
  color: #6c757d;
}
.canceled-row {
  opacity: 0.6;
}
.canceled-row td {
  text-decoration: line-through;
}
/* Bulk update panel */
.btn-select-all-filter {
  padding: 8px 14px;
  background: #0d6efd;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}
.btn-select-all-filter:hover:not(:disabled) {
  background: #0b5ed7;
}
.btn-select-all-filter:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.recompare-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #fff3cd, #ffe69c);
  border: 1px solid #ffc107;
  border-radius: 8px;
}
.recompare-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95em;
}
.recompare-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-recompare {
  padding: 8px 16px;
  background: #198754;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}
.btn-recompare:hover:not(:disabled) {
  background: #157347;
}
.btn-recompare:disabled {
  background: #6c757d;
  cursor: not-allowed;
}
.recompare-result {
  width: 100%;
  font-size: 0.95em;
}
.recompare-result.result-success {
  color: #0f5132;
}
.recompare-result.result-error {
  color: #842029;
}

.bulk-update-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #e7f1ff, #cfe2ff);
  border: 1px solid #b6d4fe;
  border-radius: 8px;
}
.bulk-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95em;
}
.btn-clear-selection {
  padding: 4px 10px;
  background: #fff;
  color: #6c757d;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.85em;
  cursor: pointer;
}
.btn-clear-selection:hover {
  color: #dc3545;
  border-color: #dc3545;
}
.bulk-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}
.bulk-input {
  flex: 1;
  min-width: 200px;
  max-width: 400px;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.95em;
}
.bulk-input:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}
.btn-bulk-update {
  padding: 8px 16px;
  background: linear-gradient(135deg, #198754, #157347);
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 0.95em;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.btn-bulk-update:hover:not(:disabled) {
  background: linear-gradient(135deg, #157347, #146c43);
}
.btn-bulk-update:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.bulk-result {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.9em;
}
.bulk-result.result-success {
  background: #d1e7dd;
  color: #0f5132;
}
.bulk-result.result-error {
  background: #f8d7da;
  color: #842029;
}
.cell-checkbox {
  width: 40px;
  text-align: center;
}
.cell-verified-header,
.cell-verified {
  width: 44px;
  text-align: center;
}
.cell-verified input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}
.cell-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.row-selected {
  background: #e7f1ff !important;
}
.row-selected:hover {
  background: #cfe2ff !important;
}
</style>
